import { useCallback, useState } from "react";
import cn from "classnames";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { Button } from "@/components/ui/buttons/button";
import { Dialog } from "@/components/ui/dialog/dialog";
import { Alert } from "@/components/alert/alert";
import { RouteNames } from "@/types/route-names";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import {
  useCreateItemDeck,
  useItemDecks,
  useUpdateItemDeck,
} from "@/hooks/queries/use-item-decks";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StarIcon } from "@/icons";
import { isXsScreen } from "@/utils/screen-size-check";
import { DECK_CARD_SLOT_COUNT, DECK_POKE_SLOT_COUNT } from "utils/item-decks/deck-validation";
import { ItemDeckData } from "types/web-app";

/**
 * Item Deck List View - Displays the player's item decks (core-item-deck-elixir).
 */
export const DeckListView = () => {
  const { setCurrentRoute, setSelectedItemDeck, previousRoute } = useSPAContext();
  const { data: decks, isLoading, isError, refetch } = useItemDecks();
  const createItemDeck = useCreateItemDeck();
  const updateItemDeck = useUpdateItemDeck();

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newDeckName, setNewDeckName] = useState("");

  const handleDeckClick = useCallback(
    (deck: ItemDeckData) => {
      setSelectedItemDeck(deck);
      setCurrentRoute(RouteNames.ITEM_DECK_EDIT);
    },
    [setCurrentRoute, setSelectedItemDeck],
  );

  const handleCreate = useCallback(async () => {
    if (createItemDeck.isPending) return;

    if (!newDeckName.trim()) {
      Alert("Deck name cannot be empty");
      return;
    }

    try {
      const response = await createItemDeck.mutateAsync({ name: newDeckName.trim() });

      if (response.ok && response.data?.deck) {
        setSelectedItemDeck({
          id: response.data.deck.id,
          name: response.data.deck.name,
          card_slots: [],
          poke_slots: [],
          pokemons: [],
          is_valid_for_match: false,
          is_main: false,
        });
        setCurrentRoute(RouteNames.ITEM_DECK_EDIT);
        setIsCreateDialogOpen(false);
        setNewDeckName("");
      } else {
        Alert(response.message || "Failed to create deck. Please try again.");
      }
    } catch (error) {
      console.error("Failed to create item deck:", error);
      Alert("Failed to create deck. Please try again.");
    }
  }, [createItemDeck, newDeckName, setCurrentRoute, setSelectedItemDeck]);

  const handleSetMainDeck = useCallback(
    async (deck: ItemDeckData) => {
      if (deck.is_main || updateItemDeck.isPending) return;
      try {
        const response = await updateItemDeck.mutateAsync({
          deck_id: deck.id,
          set_main_deck: true,
        });
        if (!response.ok) {
          Alert(response.message || "Failed to set main deck");
        }
      } catch (error) {
        console.error("Failed to set main deck:", error);
        Alert("Failed to set main deck");
      }
    },
    [updateItemDeck],
  );

  return (
    <div className="relative overflow-visible w-full min-h-screen bg-black/50 backdrop-blur-[5px]">
      <div className="flex flex-col w-full relative pb-12 overflow-visible">
        <div className="relative flex items-center justify-between w-full px-4 mt-4">
          <div className="absolute hidden xs:block h-1/2 w-full left-0 top-1/2 -translate-y-1/2 bg-pk-gray-1"></div>
          <StrokedText strokeWidth={12} fontSize={24} className="z-10">
            MY DECKS
          </StrokedText>

          <Button
            variant="blue"
            className="z-10"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            New deck
          </Button>
        </div>

        <div className="flex flex-col px-2 gap-3 mt-3 xs:mt-2">
          {isLoading && (
            <StandardFrame variant="black" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 animate-pulse">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  Loading decks...
                </StrokedText>
              </div>
            </StandardFrame>
          )}

          {!isLoading && isError && (
            <StandardFrame variant="red" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 text-center">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  Failed to load decks
                </StrokedText>
                <Button variant="red" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </StandardFrame>
          )}

          {!isLoading && !isError && decks && decks.length > 0 && (
            <div className="max-h-[85vh] flex flex-col gap-3 grow overflow-y-auto px-1 xs:px-2 pb-2">
              {decks.map((deck) => (
                <DeckCard
                  key={deck.id}
                  deck={deck}
                  onClick={handleDeckClick}
                  onSetMain={handleSetMainDeck}
                />
              ))}
            </div>
          )}

          {!isLoading && !isError && decks && decks.length === 0 && (
            <StandardFrame variant="blue" className="w-full">
              <div className="p-4 flex flex-col items-center justify-center py-8 text-center">
                <StrokedText strokeWidth={10} fontSize={20} className="z-10">
                  No decks yet
                </StrokedText>
                <p className="text-pk-gray-4 text-sm mt-2 max-w-sm">
                  Create your first item deck to bring items into battle.
                </p>
                <Button variant="blue" className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                  Create deck
                </Button>
              </div>
            </StandardFrame>
          )}
        </div>

        <CreateDeckDialog
          isOpen={isCreateDialogOpen}
          onClose={() => {
            setIsCreateDialogOpen(false);
            setNewDeckName("");
          }}
          deckName={newDeckName}
          onDeckNameChange={setNewDeckName}
          onCreate={handleCreate}
          isCreating={createItemDeck.isPending}
        />
      </div>
    </div>
  );
};

const DeckCard = ({
  deck,
  onClick,
  onSetMain,
}: {
  deck: ItemDeckData;
  onClick: (deck: ItemDeckData) => void;
  onSetMain: (deck: ItemDeckData) => void;
}) => {
  const pokemons = deck.pokemons ?? [];
  const isValid = deck.is_valid_for_match;
  const slots = Array.from({ length: DECK_POKE_SLOT_COUNT }, (_, i) => pokemons[i] ?? null);

  return (
    <div className="group relative shadow-cartoon border-4 border-gray-900 rounded-xl overflow-hidden transition-all duration-200">
      {/* Main card with gradient background */}
      <div className="relative bg-gradient-to-br from-pk-night-2 to-pk-night-1 p-3">
        {/* Texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "url('/assets/app-ui/textre.png')",
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
          }}
        />

        {/* Skewed accent bar */}
        <div
          className={cn(
            "top-0 right-0 bottom-0 absolute w-1/3 skew-x-[-20deg] translate-x-8 pointer-events-none bg-gradient-to-l to-transparent",
            deck.is_main ? "from-pk-yellow-1/20" : "from-pk-blue-1/20",
          )}
        />

        {/* Header row */}
        <button
          type="button"
          onClick={() => onClick(deck)}
          className="z-10 relative flex justify-between items-center gap-2 mb-3 w-full text-left cursor-pointer"
        >
          <div className="flex-1 min-w-0">
            <h3 className="drop-shadow-[2px_2px_0_rgba(0,0,0,0.8)] font-black text-white text-xl xs:text-2xl truncate italic uppercase tracking-wider">
              {deck.name}
            </h3>
          </div>

          <div
            className={cn(
              "shrink-0 shadow-cartoon-sm px-2 py-0.5 rounded-sm font-bold text-[10px] uppercase tracking-wider",
              isValid ? "bg-pk-green-2 text-white" : "bg-pk-gray-3 text-pk-gray-5",
            )}
          >
            {isValid ? "Ready" : "Incomplete"}
          </div>
        </button>

        {/* Pokemon grid */}
        <button
          type="button"
          onClick={() => onClick(deck)}
          className="z-10 relative gap-1 grid grid-cols-4 w-full cursor-pointer"
        >
          {slots.map((pokemon, index) => (
            <div
              key={pokemon ? pokemon.id : `empty-${index}`}
              className={cn(
                "relative flex justify-center items-center border-2 rounded-sm h-14 transition-all",
                pokemon
                  ? "bg-pk-blue-1/30 border-pk-blue-2 group-hover:bg-pk-blue-1/50"
                  : "bg-pk-gray-1/30 border-pk-gray-2",
              )}
            >
              {pokemon ? (
                <PokemonSprite
                  pokemon={pokemon}
                  className="w-12 h-12"
                  style={{ scale: isXsScreen() ? 1.15 : 1.4 }}
                  ignoreScale
                />
              ) : (
                <span className="opacity-30 text-pk-gray-4 text-lg">—</span>
              )}
            </div>
          ))}
        </button>

        {/* Footer: item count + main deck action */}
        <div className="z-10 relative flex justify-between items-center gap-2 mt-3">
          <div className="shadow-cartoon-sm bg-pk-gray-1/60 px-2 py-0.5 rounded-sm font-bold text-pk-gray-5 text-[10px] uppercase tracking-wider">
            {deck.card_slots.length}/{DECK_CARD_SLOT_COUNT} Cards
          </div>

          <button
            type="button"
            onClick={() => onSetMain(deck)}
            disabled={deck.is_main}
            className={cn(
              "flex items-center gap-1 shadow-cartoon-sm px-2 py-0.5 rounded-sm font-bold text-[10px] uppercase tracking-wider transition-colors",
              deck.is_main
                ? "bg-pk-yellow-1 text-pk-night-1 cursor-default"
                : "bg-pk-gray-3 text-pk-gray-5 hover:bg-pk-gray-2",
            )}
          >
            <StarIcon width={10} height={10} fill="currentColor" />
            {deck.is_main ? "Main Deck" : "Set as Main"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateDeckDialog = ({
  isOpen,
  onClose,
  deckName,
  onDeckNameChange,
  onCreate,
  isCreating,
}: {
  isOpen: boolean;
  onClose: () => void;
  deckName: string;
  onDeckNameChange: (name: string) => void;
  onCreate: () => void;
  isCreating: boolean;
}) => {
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isCreating && deckName.trim()) {
      onCreate();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} closeOnBackdrop={!isCreating}>
      <div className="w-[85vw] max-w-md mx-auto">
        <StandardFrame variant="blue" className="w-full">
          <div className="p-4 text-center">
            <StrokedText strokeWidth={10} fontSize={18} className="z-10">
              CREATE DECK
            </StrokedText>
            <p className="text-pk-gray-4 mt-2 mb-6 text-sm">
              Give your item deck a name to get started.
            </p>

            <div className="mb-6">
              <div className="flex flex-row items-center gap-2 bg-pk-blue-3/50 rounded px-3 py-2 border-2 border-pk-blue-4">
                <input
                  type="text"
                  value={deckName}
                  onChange={(e) => onDeckNameChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Deck name..."
                  className="w-full text-lg bg-transparent outline-none border-none text-pk-white placeholder-pk-gray-4 text-center font-semibold"
                  maxLength={20}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
              <p className="text-xs text-pk-gray-4 mt-2">{deckName.length}/20</p>
            </div>

            <div className="flex gap-4 items-center justify-center w-full">
              <Button variant="gray" onClick={onClose} disabled={isCreating} className="flex-1">
                CANCEL
              </Button>
              <Button
                variant="blue"
                onClick={onCreate}
                disabled={isCreating || !deckName.trim()}
                className="flex-1"
              >
                {isCreating ? "CREATING..." : "CREATE DECK"}
              </Button>
            </div>
          </div>
        </StandardFrame>
      </div>
    </Dialog>
  );
};
